<template>
  <div class="w-full flex h-full divide-x-2 divide-grey">
    <div class="w-[60%] h-full overflow-y-auto pr-7">
      <div class="w-full grid content-start gap-3">
        <h3 class="text-lg font-semibold mb-2">Profile</h3>

        <TlTextField
          customClass="!border-primary-main"
          labelText="Email"
          type="email"
          :disabled="isDemoAccount"
          v-model="formData.username"
          size="small"
        />
        <TlTextField
          customClass="!border-primary-main"
          labelText="First Name"
          :disabled="isDemoAccount"
          v-model="formData.first_name"
          size="small"
        />
        <TlTextField
          customClass="!border-primary-main"
          labelText="Last Name"
          :disabled="isDemoAccount"
          v-model="formData.last_name"
          size="small"
        />
        <TlSelect
          customClass="!border-primary-main"
          labelText="Affiliation"
          placeholder="Select an affiliation"
          v-model="selectedAffiliation"
          :items="affiliations"
          size="small"
        />

        <div class="flex justify-end space-x-5 mt-5">
          <v-btn
            @click="resetProfileUpdate"
            variant="flat"
            class="!py-3 !bg-primary-light !rounded-xl !tracking-normal !capitalize !text-black !h-auto min-w-[120px]"
          >
            Cancel
          </v-btn>

          <v-btn
            @click="updateProfile"
            :disabled="
              !formData.username ||
              !formData.first_name ||
              !formData.last_name ||
              isDemoAccount ||
              loadingResponse
            "
            :loading="loadingResponse"
            variant="flat"
            class="!py-3 !bg-primary-main !rounded-xl !tracking-normal !capitalize !text-white !h-auto min-w-[120px]"
          >
            Update Profile
          </v-btn>
        </div>
      </div>

      <div class="w-full grid content-start gap-3">
        <h3 class="text-lg font-semibold mb-2">Security</h3>

        <TlTextField
          type="password"
          customClass="!border-primary-main"
          labelText="Current Password"
          :disabled="isDemoAccount"
          v-model="passwordFormData.old_password"
          size="small"
        />
        <TlTextField
          type="password"
          customClass="!border-primary-main"
          labelText="New Password"
          :disabled="isDemoAccount"
          v-model="passwordFormData.new_password"
          size="small"
        />
        <TlTextField
          type="password"
          customClass="!border-primary-main"
          labelText="Confirm Password"
          :disabled="isDemoAccount"
          v-model="passwordFormData.confirm_password"
          size="small"
        />

        <div class="flex justify-end space-x-5 mt-5">
          <v-btn
            variant="flat"
            class="!py-3 !bg-primary-light !rounded-xl !tracking-normal !capitalize !text-black !h-auto min-w-[120px]"
          >
            Cancel
          </v-btn>

          <v-btn
            @click="updatePassword"
            :disabled="
              !passwordFormData.old_password ||
              !passwordFormData.new_password ||
              !passwordFormData.confirm_password ||
              isDemoAccount ||
              loadingPasswordResponse
            "
            :loading="loadingPasswordResponse"
            variant="flat"
            class="!py-3 !bg-primary-main !rounded-xl !tracking-normal !capitalize !text-white !h-auto min-w-[120px]"
          >
            Update Password
          </v-btn>
        </div>
      </div>
    </div>

    <div class="w-[40%] h-full overflow-y-auto pl-7">
      <div class="w-full grid content-start gap-5 max-w-[300px] mx-auto">
        <h3 class="text-lg font-semibold">Subscriptions</h3>

        <v-btn
          @click="openSubscription"
          variant="flat"
          class="!py-3 !bg-primary-main !rounded-xl !tracking-normal !capitalize !text-white !h-auto"
        >
          <template v-slot:prepend>
            <Icon icon="ant-design:link-outlined" class="text-xl" />
          </template>
          Manage Subscription
        </v-btn>
        <p class="">
          If your account is administered by a university, company, or other institution, please
          contact your administrator or
          <a href="mailto:support@writewise.io" class="text-primary-main" target="_blank"
            >support@writewise.io</a
          >
          to modify your subscription information.
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import TlSelect from '@/components/global/TlSelect.vue';
import TlTextField from '@/components/global/TlTextField.vue';
import { Icon } from '@iconify/vue';
import { usePayloadStore } from '@/stores/payload';
import { getProfile, updateProfile, updatePassword } from '@/actions/auth';
import Message from 'antd-message';
import { Vue, Options } from 'vue-class-component';
import { Affiliations } from '@/utils/configs';

@Options({
  components: {
    TlTextField,
    TlSelect,
    Icon
  }
})
export default class Account extends Vue {
  mounted() {
    this.getProfile();
  }

  private payloadStore = usePayloadStore();

  public loadingResponse = false;

  public loadingPasswordResponse = false;

  public formData = {
    username: '',
    first_name: '',
    last_name: ''
  };

  public selectedAffiliation: any = null;

  public affiliation = '';

  public affiliations: any = Affiliations.en;
  public affiliationsEs: any = Affiliations.es;

  get isDemoAccount() {
    return (
      this.formData.username === 'WriteWise_Demo' ||
      this.formData.username === 'contact@writewise.io'
    );
  }

  public passwordFormData = {
    old_password: '',
    new_password: '',
    confirm_password: ''
  };

  public resetProfileUpdate = () => {
    this.getProfile();
  };

  private getProfile = () => {
    this.payloadStore.change({ loadingResponse: true });
    getProfile(this.payloadStore.payload.user.pk)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then((res: any) => {
        this.payloadStore.change({ loadingResponse: false });
        this.formData = res.user;
        const affiliation = this.affiliations.find(
          (a: any) => a.title === res.affiliation
        );
        const affiliationEs = this.affiliationsEs.find(
          (a: any) => a.title === res.affiliation
        );
        if (affiliation) this.selectedAffiliation = affiliation.value;
        else if (affiliationEs) this.selectedAffiliation = affiliationEs.value;
        else this.affiliation = res.affiliation;

        Message.success('User fetched successfully');
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .catch((err) => {
        this.payloadStore.change({ loadingResponse: false });
        Message.error('User could not be fetched');
      });
  };

  public updateProfile = () => {
    this.loadingResponse = true;
    const affiliation = this.affiliations.find(
      (affiliation: any) => affiliation.value === this.selectedAffiliation
    );
    updateProfile(this.payloadStore.payload.user.pk, {
      user: this.formData,
      affiliation: affiliation.title || this.affiliation
    })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then((res: any) => {
        this.loadingResponse = false;
        this.formData = res.user;
        Message.success('User updated successfully');
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .catch((err) => {
        this.loadingResponse = false;
        Message.error('User could not be updated');
      });
  };

  public updatePassword = () => {
    this.loadingPasswordResponse = true;
    if (this.passwordFormData.old_password === this.passwordFormData.new_password) {
      Message.error('Old Password used again. Enter a new password');
      this.loadingPasswordResponse = false;
      return;
    }
    if (this.passwordFormData.new_password !== this.passwordFormData.confirm_password) {
      Message.error('Password Mismatch');
      this.loadingPasswordResponse = false;
      return;
    }
    updatePassword({
      old_password: this.passwordFormData.old_password,
      new_password: this.passwordFormData.new_password
    })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then((res: any) => {
        this.loadingPasswordResponse = false;
        Message.success('Password updated successfully');
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .catch((err) => {
        this.loadingPasswordResponse = false;
        Message.error('Password could not be updated');
      });
  };

  public openSubscription() {
    (window.open('https://writewise.onfastspring.com/account', '_blank') as any).focus();
  }
}
</script>
